import React from "react";
import { CreateRegionalBlock, MedicationGroup } from "components";
import {
  UpdateCardProps,
  useAuth,
  useInitializeOfflineData,
  useParams
} from "duck";
import {
  RegionalBlockTypeEnum,
  useGetAnestheticProtocolQuery,
  UserRoleEnum,
  useUpdateAnestheticProtocolRegionalBlockMutation
} from "requests";
import {
  useCreateAnestheticProtocolRegionalBlock,
  useDeleteMedications
} from "./duck";

const RegionalBlocks: React.FC = () => {
  const { id: anestheticProtocolId } = useParams();
  const { data: protocol } = useGetAnestheticProtocolQuery({
    variables: { _id: +anestheticProtocolId }
  });
  const { loadProtocols } = useInitializeOfflineData();
  const [{ user }] = useAuth();

  const regionalOralBlocks =
    protocol?.anestheticProtocol?.regionalBlocks?.collection.filter(
      block => block.type === RegionalBlockTypeEnum.RegionalOralBlocks
    ) || [];
  const epiduralCalculations =
    protocol?.anestheticProtocol?.regionalBlocks?.collection.filter(
      block => block.type === RegionalBlockTypeEnum.EpiduralCalculations
    ) || [];

  const deleteMedications = useDeleteMedications();
  const [update] = useUpdateAnestheticProtocolRegionalBlockMutation({
    ignoreResults: true
  });
  const create = useCreateAnestheticProtocolRegionalBlock();

  const updateMedication = React.useCallback(
    (props: UpdateCardProps) => {
      const { idOrFakeId, ...data } = props;

      if (typeof idOrFakeId === "number") {
        return update({
          variables: { input: { _id: idOrFakeId, ...data } }
        }).then(loadProtocols);
      }
    },
    [update]
  );

  const defaultProtocol =
    protocol?.anestheticProtocol?.defaultProtocol &&
    user.userRole !== UserRoleEnum.RoleOrganizationAdmin;

  if (!protocol?.anestheticProtocol) {
    return null;
  }

  return (
    <MedicationGroup.Provider update={updateMedication}>
      <MedicationGroup
        groupName="Epidural/Regional Blocks"
        medications={epiduralCalculations}
        medicationType={RegionalBlockTypeEnum.EpiduralCalculations}
        create={
          !defaultProtocol ? (
            <CreateRegionalBlock
              successToast="Еpidural Calculations Medication is created successfully."
              onSubmit={values =>
                create(values, RegionalBlockTypeEnum.EpiduralCalculations)
              }
              isProtocol
            />
          ) : (
            undefined
          )
        }
        onDelete={!defaultProtocol ? deleteMedications : undefined}
        expandedCards={false}
        hasTimeAdministered={false}
        viewOnly={defaultProtocol}
      />
      <MedicationGroup
        groupName="Regional Oral Blocks"
        medications={regionalOralBlocks}
        medicationType={RegionalBlockTypeEnum.RegionalOralBlocks}
        create={
          !defaultProtocol ? (
            <CreateRegionalBlock
              successToast="Regional Oral Blocks Medication is created successfully."
              onSubmit={values =>
                create(values, RegionalBlockTypeEnum.RegionalOralBlocks)
              }
              isProtocol
            />
          ) : (
            undefined
          )
        }
        onDelete={!defaultProtocol ? deleteMedications : undefined}
        expandedCards={false}
        hasTimeAdministered={false}
        viewOnly={defaultProtocol}
      />
    </MedicationGroup.Provider>
  );
};

export default RegionalBlocks;
